// Shared frontend template-media validation (F1/F2/F4 — see
// docs/specs/2026-09-10-template-media-validation/spec.md).
//
// Bounded signature identification only: this is not integrity, codec or
// malware validation. A complete identifying header followed by a
// corrupt/truncated body may still pass.

const PREFIX_BYTES = 65536;

export const TEMPLATE_MEDIA_POLICY = {
  JPEG: { mime: "image/jpeg", maxBytes: 5_000_000, category: "IMAGE" },
  PNG: { mime: "image/png", maxBytes: 5_000_000, category: "IMAGE" },
  MP4: { mime: "video/mp4", maxBytes: 16_000_000, category: "VIDEO" },
  PDF: { mime: "application/pdf", maxBytes: 100_000_000, category: "DOCUMENT" },
};

export const TEMPLATE_MEDIA_ERROR_CODES = {
  UNSUPPORTED_FORMAT: "UNSUPPORTED_FORMAT",
  EMPTY_FILE: "EMPTY_FILE",
  OVERSIZED: "OVERSIZED",
  IDENTIFIER_MISMATCH: "IDENTIFIER_MISMATCH",
  UNIDENTIFIED_FORMAT: "UNIDENTIFIED_FORMAT",
  READ_FAILURE: "READ_FAILURE",
};

export class TemplateMediaValidationError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "TemplateMediaValidationError";
    this.code = code;
  }
}

const MESSAGES = {
  UNSUPPORTED_FORMAT: "Formato no permitido. Selecciona JPEG, PNG, MP4 o PDF.",
  IDENTIFIER_MISMATCH:
    "La extensión, el tipo y el contenido no coinciden. Selecciona el archivo original.",
  EMPTY_FILE: "El archivo está vacío. Selecciona otro archivo.",
  UNIDENTIFIED_FORMAT:
    "No pudimos identificar el formato. Exporta de nuevo el archivo y vuelve a seleccionarlo.",
  READ_FAILURE: "No pudimos leer el archivo. Vuelve a seleccionarlo.",
};

const buildOversizedMessage = (maxBytes) =>
  `El archivo supera el límite de ${maxBytes / 1_000_000} MB (${maxBytes.toLocaleString(
    "en-US"
  )} bytes).`;

const fail = (code, message) => {
  throw new TemplateMediaValidationError(code, message ?? MESSAGES[code]);
};

const EXTENSION_TO_FORMAT = { jpg: "JPEG", jpeg: "JPEG", png: "PNG", mp4: "MP4", pdf: "PDF" };

const getFinalExtension = (filename) => {
  if (typeof filename !== "string") return "";
  const match = /\.([^.]+)$/.exec(filename);
  return match ? match[1].toLowerCase() : "";
};

const readUint32BE = (bytes, offset) =>
  ((bytes[offset] << 24) | (bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3]) >>>
  0;

const readAscii = (bytes, offset, length) =>
  String.fromCharCode(...bytes.slice(offset, offset + length));

// --- Bounded per-format signature profiles (see spec.md F2) ---

const JPEG_APP_MARKER_MIN = 0xe0;
const JPEG_APP_MARKER_MAX = 0xef;
const JPEG_DQT_MARKER = 0xdb;

const identifyJpeg = (bytes, fileSize) => {
  if (bytes.length < 6) return null;
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8 || bytes[2] !== 0xff) return null;
  const marker = bytes[3];
  const isApp = marker >= JPEG_APP_MARKER_MIN && marker <= JPEG_APP_MARKER_MAX;
  if (!isApp && marker !== JPEG_DQT_MARKER) return null;
  const length = (bytes[4] << 8) | bytes[5];
  if (length < 2) return null;
  const segmentEnd = 4 + length;
  if (segmentEnd > fileSize || segmentEnd > bytes.length) return null;
  return "JPEG";
};

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

const identifyPng = (bytes, fileSize) => {
  if (bytes.length < 33 || fileSize < 33) return null;
  if (PNG_SIGNATURE.some((value, i) => bytes[i] !== value)) return null;
  if (readUint32BE(bytes, 8) !== 13) return null;
  if (readAscii(bytes, 12, 4) !== "IHDR") return null;
  const width = readUint32BE(bytes, 16);
  const height = readUint32BE(bytes, 20);
  if (width === 0 || height === 0) return null;
  return "PNG";
};

const PDF_SUPPORTED_VERSIONS = new Set([
  "1.0", "1.1", "1.2", "1.3", "1.4", "1.5", "1.6", "1.7", "2.0",
]);

const identifyPdf = (bytes, fileSize) => {
  if (bytes.length < 9 || fileSize < 9) return null;
  if (readAscii(bytes, 0, 5) !== "%PDF-") return null;
  const version = readAscii(bytes, 5, 3);
  if (!PDF_SUPPORTED_VERSIONS.has(version)) return null;
  const terminator = bytes[8];
  if (terminator !== 0x0d && terminator !== 0x0a) return null;
  return "PDF";
};

const MP4_ALLOWED_MAJOR_BRANDS = ["isom", "iso2", "mp41", "mp42", "avc1"];
const MP4_IDENTIFYING_BRANDS = ["mp41", "mp42", "avc1"];
const MP4_FORBIDDEN_EXACT_BRANDS = new Set([
  "qt  ", "heic", "heix", "hevc", "hevx", "mif1", "msf1", "avif", "avis", "M4A ", "M4B ",
]);
const isForbiddenMp4Brand = (brand) =>
  MP4_FORBIDDEN_EXACT_BRANDS.has(brand) || brand.startsWith("3gp") || brand.startsWith("3g2");

const identifyMp4 = (bytes, fileSize) => {
  if (bytes.length < 16) return null;
  const boxSize = readUint32BE(bytes, 0);
  if (boxSize < 16 || boxSize > fileSize || boxSize > bytes.length) return null;
  if (readAscii(bytes, 4, 4) !== "ftyp") return null;
  const majorBrand = readAscii(bytes, 8, 4);
  const compatibleLength = boxSize - 16;
  if (compatibleLength % 4 !== 0) return null;
  const compatibleBrands = [];
  for (let offset = 16; offset < boxSize; offset += 4) {
    compatibleBrands.push(readAscii(bytes, offset, 4));
  }
  const allBrands = [majorBrand, ...compatibleBrands];
  if (allBrands.some(isForbiddenMp4Brand)) return null;
  if (!MP4_ALLOWED_MAJOR_BRANDS.includes(majorBrand)) return null;
  if (!allBrands.some((brand) => MP4_IDENTIFYING_BRANDS.includes(brand))) return null;
  return "MP4";
};

export const identifyTemplateMedia = (prefix, fileSize) => {
  if (!prefix || typeof fileSize !== "number") return null;
  const bytes = prefix instanceof Uint8Array ? prefix : new Uint8Array(prefix);
  return (
    identifyJpeg(bytes, fileSize) ||
    identifyPng(bytes, fileSize) ||
    identifyPdf(bytes, fileSize) ||
    identifyMp4(bytes, fileSize) ||
    null
  );
};

const readPrefix = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error || new Error("read error"));
    reader.onabort = () => reject(new Error("read aborted"));
    reader.readAsArrayBuffer(file.slice(0, PREFIX_BYTES));
  });

export const validateTemplateMedia = async (file, expectedCategory) => {
  if (!file || typeof file.size !== "number" || typeof file.name !== "string")
    fail(TEMPLATE_MEDIA_ERROR_CODES.UNSUPPORTED_FORMAT);

  const format = EXTENSION_TO_FORMAT[getFinalExtension(file.name)];
  if (!format) fail(TEMPLATE_MEDIA_ERROR_CODES.UNSUPPORTED_FORMAT);

  const profile = TEMPLATE_MEDIA_POLICY[format];

  if (file.size === 0) fail(TEMPLATE_MEDIA_ERROR_CODES.EMPTY_FILE);
  if (file.size > profile.maxBytes)
    fail(TEMPLATE_MEDIA_ERROR_CODES.OVERSIZED, buildOversizedMessage(profile.maxBytes));

  const declaredMime = file.type || "";
  const isJpegAlias = format === "JPEG" && declaredMime === "image/jpg";
  if (declaredMime && declaredMime !== profile.mime && !isJpegAlias)
    fail(TEMPLATE_MEDIA_ERROR_CODES.IDENTIFIER_MISMATCH);

  let prefix;
  try {
    prefix = await readPrefix(file);
  } catch {
    fail(TEMPLATE_MEDIA_ERROR_CODES.READ_FAILURE);
  }

  const identifiedFormat = identifyTemplateMedia(prefix, file.size);
  if (!identifiedFormat) fail(TEMPLATE_MEDIA_ERROR_CODES.UNIDENTIFIED_FORMAT);
  if (identifiedFormat !== format) fail(TEMPLATE_MEDIA_ERROR_CODES.IDENTIFIER_MISMATCH);

  if (expectedCategory && expectedCategory !== profile.category)
    fail(TEMPLATE_MEDIA_ERROR_CODES.IDENTIFIER_MISMATCH);

  const needsWrap = declaredMime !== profile.mime;
  const canonicalFile = needsWrap
    ? new File([file], file.name, { type: profile.mime, lastModified: file.lastModified })
    : file;

  return { file: canonicalFile, format, mime: profile.mime, category: profile.category };
};
